import React, { useEffect, useMemo, useRef, useState } from "react";
import Loading from "../../../../components/Loading";
import GuaranteeDescription from "../../../../components/GuaranteeDescription";
import InlineAlert from "../../../../components/InlineAlert";
import CustomSelect, { SelectOptionType } from "../CustomSelect";

import {
  Overlay,
  ModalContainer,
  ModalHeader,
  ModalBody,
  FilesList,
  FileItem,
  FileInfo,
  FileActions,
  PreviewContainer,
  CloseButton,
  TopBar,
  UploadButton,
  HiddenFileInput,
  HelperText,
  EmptyState,
  FormGrid,
  Field,
  Input,
  PrimaryButton,
  SecondaryButton,
  ActionsRow,
  ErrorText,
  SuccessText,
  InfoCard,
  FilesSection,
  CenterLoading,
  InitialLoadingWrap,
} from "./styles";

import { FiX, FiEye, FiDownload, FiTrash2, FiUpload, FiSend } from "react-icons/fi";
import api from "../../../../services/api";
import { ConfirmDialog } from "../ConfirmDialog";

interface Props {
  rentProcessId: number;
  propertyId: number;
  onClose: () => void;
  title?: string;
  isToSendExpressOffer?: boolean;
}

interface IUserFile {
  id: number;
  original_name: string;
  mime_type: string;
}

interface IGuarantee {
  id: number;
  name: string;
  description: string;
  documents: string[];
}

const CIVIL_STATE_OPTIONS = ["Solteiro(a)", "Casado(a)", "Separado(a)", "Divorciado(a)", "Viúvo(a)"];

export default function DocumentsModal({
  rentProcessId,
  propertyId,
  onClose,
  title,
  isToSendExpressOffer = false,
}: Props) {
  const [files, setFiles] = useState<IUserFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [loadingGuarantees, setLoadingGuarantees] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);

  const [previewFile, setPreviewFile] = useState<(IUserFile & { url: string }) | null>(null);
  const [uiError, setUiError] = useState<string>("");
  const [uiSuccess, setUiSuccess] = useState<string>("");

  const [showConfirm, setShowConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<IUserFile | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [civilState, setCivilState] = useState<string>("");
  const [profession, setProfession] = useState<string>("");
  const [guarantees, setGuarantees] = useState<IGuarantee[]>([]);
  const [selectedGuaranteeId, setSelectedGuaranteeId] = useState<number | "">("");

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string>("");
  const [sendSuccess, setSendSuccess] = useState<string>("");

  const basePath = "/api/client/tenant/documents/files";
  const uploadPath = "/api/client/tenant/documents";
  const sendPath = "/api/client/tenant/documents/send";
  const guaranteesPath = `/api/client/tenant/guarantees/${propertyId}`;

  const guaranteeObject = useMemo(() => {
    if (!selectedGuaranteeId) return undefined;
    return guarantees.find((g) => g.id === selectedGuaranteeId);
  }, [guarantees, selectedGuaranteeId]);

  const guaranteeOptions: SelectOptionType[] = useMemo(
    () => guarantees.map((g) => ({ value: String(g.id), label: g.name })),
    [guarantees]
  );

  const civilStateOptions: SelectOptionType[] = useMemo(
    () => CIVIL_STATE_OPTIONS.map((s) => ({ value: s, label: s })),
    []
  );

  async function loadFiles() {
    setLoadingFiles(true);
    try {
      const response = await api.get(basePath, { params: { rent_process_id: rentProcessId } });
      const processes = response.data?.data ?? [];

      const flattened: IUserFile[] = processes.flatMap((p: any) =>
        (p.documents ?? []).flatMap((d: any) => d.files ?? [])
      );

      setFiles(flattened);
    } catch (err) {
      console.error(err);
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }

  async function loadGuarantees() {
    setLoadingGuarantees(true);
    try {
      const response = await api.get(guaranteesPath);
      const list: IGuarantee[] = response.data?.data ?? [];

      if (list.length > 0) {
        setGuarantees(list);
        setSelectedGuaranteeId(list[0].id);
      } else {
        const fallback: IGuarantee = {
          id: 1,
          name: "Default",
          description: "Default",
          documents: ["RG e CPF ou CNH", "Comprovante de renda", "Comprovante de residência"],
        };
        setGuarantees([fallback]);
        setSelectedGuaranteeId(fallback.id);
      }
    } catch (err) {
      console.error(err);
      const fallback: IGuarantee = {
        id: 1,
        name: "Default",
        description: "Default",
        documents: ["RG e CPF ou CNH", "Comprovante de renda", "Comprovante de residência"],
      };
      setGuarantees([fallback]);
      setSelectedGuaranteeId(fallback.id);
    } finally {
      setLoadingGuarantees(false);
    }
  }

  useEffect(() => {
    let alive = true;

    async function boot() {
      setInitialLoading(true);
      setPreviewFile(null);
      setSendError("");
      setSendSuccess("");

      await Promise.all([loadFiles(), loadGuarantees()]);

      if (alive) setInitialLoading(false);
    }

    boot();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentProcessId, propertyId]);

  async function getFileUrl(fileId: number, download = false) {
    const response = await api.get(`${basePath}/${fileId}`, {
      params: download ? { download: 1 } : {},
    });
    return response.data.url as string;
  }

  async function handlePreview(file: IUserFile) {
    try {
      const url = await getFileUrl(file.id);
      setPreviewFile({ ...file, url });
    } catch (err) {
      console.error(err);
      setUiError("Erro ao carregar pré-visualização, tente novamente mais tarde.");
    }
  }

  async function handleDownload(file: IUserFile) {
    try {
      const url = await getFileUrl(file.id, true);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.original_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
      setUiError("Erro ao baixar documento, tente novamente mais tarde.");
    }
  }

  function renderPreview() {
    if (!previewFile) return null;

    if (previewFile.mime_type === "application/pdf") {
      return <iframe src={previewFile.url} title="PDF Preview" />;
    }
    if (previewFile.mime_type.startsWith("image/")) {
      return <img src={previewFile.url} alt={previewFile.original_name} />;
    }
    return <p>Pré-visualização indisponível</p>;
  }

  function askDelete(file: IUserFile) {
    setFileToDelete(file);
    setShowConfirm(true);
  }

  async function confirmDelete() {
    if (!fileToDelete) return;

    try {
      setDeleting(true);
      await api.delete(`${basePath}/${fileToDelete.id}`);

      setFiles((prev) => prev.filter((f) => f.id !== fileToDelete.id));
      if (previewFile?.id === fileToDelete.id) setPreviewFile(null);

      setShowConfirm(false);
      setFileToDelete(null);
    } catch (err) {
      console.error(err);
      setUiError("Erro ao excluir documento. Tente novamente mais tarde.");
    } finally {
      setDeleting(false);
    }
  }

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  async function handlePickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files;
    if (!picked || picked.length === 0) return;

    try {
      setUploading(true);

      const form = new FormData();
      form.append("rent_process_id", String(rentProcessId));
      form.append("property_id", String(propertyId));

      Array.from(picked).forEach((file) => {
        form.append("documents[]", file);
      });

      await api.post(uploadPath, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      await loadFiles();
    } catch (err) {
      console.error(err);
      setUiError("Erro ao enviar documentos. Tente novamente mais tarde.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function handleSendDocumentation() {
    setSendError("");
    setSendSuccess("");

    if (!civilState) return setSendError("Estado civil é obrigatório.");
    if (!profession.trim()) return setSendError("Ocupação é obrigatória.");
    if (!selectedGuaranteeId) return setSendError("Garantia é obrigatória.");
    if (files.length === 0) return setSendError("Envie ao menos um documento antes de enviar para análise.");

    try {
      setSending(true);

      const response = await api.post(sendPath, {
        material_status: civilState,
        profession,
        express_offer: isToSendExpressOffer ? 1 : 0,
        rent_process_id: rentProcessId,
        guarantee_id: selectedGuaranteeId,
      });

      if (response.data?.success) setSendSuccess("Documentação enviada para análise com sucesso!");
      else setSendError("Não foi possível enviar para análise. Tente novamente.");
    } catch (err) {
      console.error(err);
      setSendError("Erro ao enviar documentação para análise.");
    } finally {
      setSending(false);
    }
  }

  if (initialLoading) {
    return (
      <Overlay onClick={onClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <div>
              <h3>Documentos do processo</h3>
              {title ? <small>{title}</small> : null}
            </div>

            <CloseButton onClick={onClose}>
              <FiX size={24} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            <InitialLoadingWrap>
              <Loading />
            </InitialLoadingWrap>
          </ModalBody>
        </ModalContainer>
      </Overlay>
    );
  }

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ConfirmDialog
          open={showConfirm}
          title="Excluir documento"
          description={
            fileToDelete
              ? `Deseja excluir o arquivo "${fileToDelete.original_name}"? Essa ação não poderá ser desfeita.`
              : ""
          }
          loading={deleting}
          onCancel={() => {
            setShowConfirm(false);
            setFileToDelete(null);
          }}
          onConfirm={confirmDelete}
        />

        <ModalHeader>
          <div>
            <h3>Documentos do processo</h3>
            {title ? <small>{title}</small> : null}
          </div>

          <CloseButton onClick={onClose}>
            <FiX size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <TopBar>
            {guaranteeObject ? (
              <InfoCard>
                <label>Documentos exigidos</label>
                <div className="items">
                  {guaranteeObject.documents.map((d) => (
                    <div key={d}>• {d}</div>
                  ))}
                </div>
              </InfoCard>
            ) : null}

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <UploadButton type="button" onClick={openFilePicker} disabled={uploading}>
                <FiUpload />
                {uploading ? "Enviando..." : "Adicionar documentos"}
              </UploadButton>

              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handlePickFiles}
                accept="application/pdf,image/*"
              />
            </div>

            <HelperText>
              Envie PDF ou imagens (JPG/PNG). Depois, preencha os dados e clique em “Enviar para análise”.
            </HelperText>

            {uiError && (
              <InlineAlert type="error" onClose={() => setUiError("")}>
                {uiError}
              </InlineAlert>
            )}

            {uiSuccess && (
              <InlineAlert type="success" onClose={() => setUiSuccess("")}>
                {uiSuccess}
              </InlineAlert>
            )}

            <FormGrid>
              <Field>
                <label>Garantia</label>
                <CustomSelect
                  value={selectedGuaranteeId ? String(selectedGuaranteeId) : ""}
                  options={guaranteeOptions}
                  placeholder={loadingGuarantees ? "Carregando..." : "Selecione"}
                  onChange={(val) => setSelectedGuaranteeId(val ? Number(val) : "")}
                />
              </Field>

              <Field>
                <label>Estado civil</label>
                <CustomSelect
                  value={civilState}
                  options={civilStateOptions}
                  placeholder="Selecione"
                  onChange={(val) => setCivilState(val)}
                />
              </Field>

              <Field style={{ gridColumn: "1 / -1" }}>
                <label>Ocupação</label>
                <Input value={profession} onChange={(e) => setProfession(e.target.value)} placeholder="Ex: Desenvolvedor" />
              </Field>

              {guaranteeObject ? (
                <Field style={{ gridColumn: "1 / -1" }}>
                  <label>Descrição da garantia</label>

                  <div
                    style={{
                      background: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                      padding: 12,
                      marginBottom: 10,
                    }}
                  >
                    <GuaranteeDescription
                      description={guaranteeObject.description}
                    />
                  </div>
                </Field>
              ) : null}
            </FormGrid>

            {sendError ? <ErrorText>{sendError}</ErrorText> : null}
            {sendSuccess ? <SuccessText>{sendSuccess}</SuccessText> : null}

            <ActionsRow>
              <SecondaryButton type="button" onClick={onClose}>
                Fechar
              </SecondaryButton>

              <PrimaryButton type="button" onClick={handleSendDocumentation} disabled={sending}>
                <FiSend />
                {sending ? "Enviando..." : "Enviar para análise"}
              </PrimaryButton>
            </ActionsRow>
          </TopBar>

          <FilesSection>
            {loadingFiles ? (
              <CenterLoading>
                <Loading />
              </CenterLoading>
            ) : files.length === 0 ? (
              <EmptyState>
                <p>Nenhum documento encontrado para este processo.</p>
              </EmptyState>
            ) : (
              <>
                <FilesList>
                  {files.map((file) => (
                    <FileItem key={file.id}>
                      <FileInfo>
                        <strong>{file.original_name}</strong>
                        <span>{file.mime_type}</span>
                      </FileInfo>

                      <FileActions>
                        <FiEye title="Visualizar" onClick={() => handlePreview(file)} />
                        <FiDownload title="Download" onClick={() => handleDownload(file)} />
                        <FiTrash2 title="Excluir" onClick={() => askDelete(file)} />
                      </FileActions>
                    </FileItem>
                  ))}
                </FilesList>

                {previewFile && <PreviewContainer>{renderPreview()}</PreviewContainer>}
              </>
            )}
          </FilesSection>
        </ModalBody>
      </ModalContainer>
    </Overlay>
  );
}
